import { balance as defaultBalance, type Balance } from './balance';
import { DEFENSE_SLOTS, OFFENSE_SLOTS, SLOT_POSITION } from './playdef';
import { createRng, type Rng } from './rng';
import { charted } from './subs';
import type { CallView, Coach, WindowView } from './game';
import type { Combatant, DefenseSlot, OffenseSlot, Sub, SubWindow, TeamId } from './types';

/** A row of balance.json `coach.situations`: when it applies, and its play weights by play id. */
export interface SituationRow {
  id: string;
  minBallOn?: number;
  maxBallOn?: number;
  downs?: readonly number[];
  minToGo?: number;
  maxToGo?: number;
  offense: Readonly<Record<string, number>>;
  defense: Readonly<Record<string, number>>;
}

/**
 * The situation row a call falls in: the first row of `coach.situations` whose conditions all
 * hold. A two-point try is the goal-line row (the first row that needs the ball on the 95).
 */
export function situationOf(view: Pick<CallView, 'game' | 'kind'>, b: Balance): SituationRow {
  const rows: readonly SituationRow[] = b.coach.situations;
  const { ballOn, down, toGo } = view.game;
  const row =
    view.kind === 'conversion'
      ? rows.find((r) => r.id === 'goal-line')
      : rows.find(
          (r) =>
            (r.minBallOn === undefined || ballOn >= r.minBallOn) &&
            (r.maxBallOn === undefined || ballOn <= r.maxBallOn) &&
            (r.downs === undefined || r.downs.includes(down)) &&
            (r.minToGo === undefined || toGo >= r.minToGo) &&
            (r.maxToGo === undefined || toGo <= r.maxToGo),
        );
  if (!row) throw new Error(`no coach row for ${view.kind} at ${ballOn}, ${down} & ${toGo}`);
  return row;
}

/**
 * A play id from the book, weighted by `weights` (play id to weight) among the plays the book
 * has; uniform over the book if it has none of them. Exactly one rng draw either way.
 */
export function pickWeighted(
  plays: readonly { id: string }[],
  weights: Readonly<Record<string, number>>,
  rng: Rng,
): string {
  if (plays.length === 0) throw new Error('the playbook is empty');
  const weighted = plays.filter((p) => (weights[p.id] ?? 0) > 0);
  if (weighted.length === 0) return (plays[rng.int(plays.length)] as { id: string }).id;
  const total = weighted.reduce((sum, p) => sum + (weights[p.id] as number), 0);
  let x = rng.next() * total;
  for (const p of weighted) {
    x -= weights[p.id] as number;
    if (x < 0) return p.id;
  }
  return (weighted.at(-1) as { id: string }).id;
}

/**
 * The scripted coach's sub window (slice 9; balance.json `coach.subs`): each Pokémon in the chart
 * in play under `hpBelow` of max HP (or at 0 HP; `hpBelow` 0 subs at 0 HP only), in slot order,
 * goes out for the healthiest bench Pokémon (in neither chart, not sitting out Rest) listed for
 * that slot's position, else the healthiest on the bench, if one is healthier than it. No Rest,
 * no bag. Draws no rng.
 */
export function rotationWindow(
  view: WindowView,
  b: Balance,
  hpBelow: number = b.coach.subs.hpBelow,
): SubWindow<string> {
  const team = view.game.teams[view.team];
  const chart = team[view.side] as Record<string, number>;
  const slots: readonly (OffenseSlot | DefenseSlot)[] =
    view.side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
  const share = (c: Combatant) => c.hp / c.stats.hp;
  const onField = charted(team);
  const taken = new Set<number>();
  const subs: Sub<string>[] = [];
  for (const slot of slots) {
    const tired = team.roster[chart[slot] as number] as Combatant;
    if (tired.hp > 0 && share(tired) >= hpBelow) continue;
    const bench = team.roster
      .map((c, i) => ({ c, i }))
      .filter(
        ({ c, i }) =>
          !onField.has(i) && !taken.has(i) && !(c.sitPlays ?? 0) && share(c) > share(tired),
      );
    const fits = bench.filter(({ c }) => !c.positions || c.positions.includes(SLOT_POSITION[slot]));
    const pool = fits.length > 0 ? fits : bench;
    let best = pool[0];
    for (const x of pool) if (best && share(x.c) > share(best.c)) best = x;
    if (!best) continue;
    taken.add(best.i);
    subs.push({ slot, in: best.i });
  }
  return subs.length > 0 ? { subs } : {};
}

export interface ScriptedCoachOptions {
  /** Rotate tired Pokémon with the bench (rotationWindow); default true. */
  rotate?: boolean;
  /** The HP share it subs under; default balance.json `coach.subs.hpBelow`; 0 = at 0 HP only. */
  hpBelow?: number;
}

/**
 * The placeholder coach until the AI (slice 7): a play by situation (balance.json `coach`), no
 * move spends; 4th down and the try are left to the game's defaults; it rotates the bench
 * (rotationWindow) unless told not to. It draws from its own rng, never the game's, so the game
 * replays from its seed and the decisions this coach made.
 */
export function scriptedCoach(seed: number, b: Balance, opts: ScriptedCoachOptions = {}): Coach {
  const rng = createRng(seed);
  const none = <S extends string>(slots: readonly S[]) =>
    Object.fromEntries(slots.map((s) => [s, null])) as Record<S, null>;
  return {
    offense: (view) => ({
      playId: pickWeighted(view.playbook.offense, situationOf(view, b).offense, rng),
      moves: none<OffenseSlot>(OFFENSE_SLOTS),
    }),
    defense: (view) => ({
      playId: pickWeighted(view.playbook.defense, situationOf(view, b).defense, rng),
      moves: none<DefenseSlot>(DEFENSE_SLOTS),
    }),
    ...(opts.rotate === false
      ? {}
      : { subs: (view: WindowView) => rotationWindow(view, b, opts.hpBelow) }),
  };
}

/** Placeholder coaches for both seats of game `seed`, each with its own rng derived from it. */
export function scriptedCoaches(
  seed: number,
  b: Balance = defaultBalance,
  opts: Partial<Record<TeamId, ScriptedCoachOptions>> = {},
): Record<TeamId, Coach> {
  return { A: scriptedCoach(2 * seed + 1, b, opts.A), B: scriptedCoach(2 * seed + 2, b, opts.B) };
}
